/*
 * Icons3D - the dimensional icon set for the landing pages.
 *
 * The flat set (AbstractIcons) draws outlines; this one draws OBJECTS. Every
 * mark is a solid in axonometric projection with shaded faces and a cast
 * shadow, so it reads as a thing sitting on the card rather than a diagram of
 * one.
 *
 * ── The three rules ────────────────────────────────────────────────────────
 *
 *   One projection.  2:1 dimetric. Half-depth is always half the half-width,
 *                    so every edge in the set sits on the same 26.57 degree
 *                    slope. This is the single biggest reason a grid of these
 *                    reads as one family.
 *   One light.        Top-left, never moved. Top faces lightest, left faces
 *                    mid, right faces darkest. Detail embossed onto a face is
 *                    drawn ON that face's slope — an upright tick on a disc is
 *                    exactly what makes a solid look flat again.
 *   One colour.       The tones are `currentColor` at four opacities, so a mark
 *                    still takes its hue from the chip it sits in and recolours
 *                    per brand for free. Baked gradients would mean 52
 *                    hand-picked palettes that would still be wrong on the
 *                    next brand.
 *
 * ── Built from primitives, not drawn one by one ────────────────────────────
 *
 * Every mark below is composed from Box / Disc / Sphere / Node / Ring and an
 * embossed detail. That is deliberate: 52 independently drawn solids drift out
 * of projection within a dozen icons, and no amount of review catches it. Fix
 * the geometry in one primitive and the whole set moves together.
 *
 * ── Sizing, and the two-tier system ───────────────────────────────────────
 *
 * These want 28px and up: below that the tones stop separating and a solid
 * reads as a blob, which is worse than the outline it replaced. So the small
 * inline spots — the 13-17px badges in hero cards, ticks in compare tables —
 * keep the flat marks from AbstractIcons. That is not a compromise, it is what
 * each set is for.
 *
 * ── Names ─────────────────────────────────────────────────────────────────
 *
 * The exports are aliased to the flat set's names at the bottom of this file,
 * so switching a page from flat to dimensional is a one-line import change and
 * the JSX stays untouched. `strokeWidth` is accepted and ignored — these have
 * no strokes, but the old call sites pass it.
 */
import type { CSSProperties, ReactNode } from 'react';

export type Icon3DProps = {
  size?: number | string;
  className?: string;
  style?: CSSProperties;
  /** Overrides currentColor for the whole object. */
  color?: string;
  /** Accepted so flat-set call sites keep working. These have no strokes. */
  strokeWidth?: number;
};

export type Icon3D = (props: Icon3DProps) => ReactNode;

/* One light direction, four tones, one ground. A mark that invents its own
   tone breaks the set, so there is nothing else to reach for. */
const TOP = { fill: 'currentColor', fillOpacity: 0.32 } as const;   // lit face
const LEFT = { fill: 'currentColor', fillOpacity: 0.58 } as const;  // half-lit
const RIGHT = { fill: 'currentColor', fillOpacity: 0.86 } as const; // shaded
const CUT = { fill: 'currentColor', fillOpacity: 0.95 } as const;   // detail / cavity
const CAST = { fill: 'currentColor', fillOpacity: 0.12 } as const;  // ground

function I3({ size = 44, className, style, color, children }: Icon3DProps & { children: ReactNode }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      color={color} shapeRendering="geometricPrecision"
      className={className} style={style} aria-hidden="true" focusable="false"
    >{children}</svg>
  );
}

/* ── Primitives ───────────────────────────────────────────────────────────── */

/** The ground an object sits on. Without it a solid looks pasted onto the card. */
const Ground = ({ cx = 12, cy = 20.3, rx = 7.2, ry = 1.7 }: Record<string, number>) => (
  <ellipse cx={cx} cy={cy} rx={rx} ry={ry} {...CAST} />
);

/** A cuboid. `y0` is the top-back corner; depth is locked to half the width. */
const Box = ({ cx = 12, y0 = 5, hw = 6, h = 7 }: Record<string, number>) => {
  const hd = hw / 2, m = y0 + hd, b = y0 + 2 * hd;
  return (
    <>
      <path d={`M${cx} ${y0}L${cx + hw} ${m}L${cx} ${b}L${cx - hw} ${m}Z`} {...TOP} />
      <path d={`M${cx - hw} ${m}L${cx} ${b}L${cx} ${b + h}L${cx - hw} ${m + h}Z`} {...LEFT} />
      <path d={`M${cx} ${b}L${cx + hw} ${m}L${cx + hw} ${m + h}L${cx} ${b + h}Z`} {...RIGHT} />
    </>
  );
};

/** A cylinder. The lit top ellipse over one shaded wall reads as round. */
const Disc = ({ cx = 12, cy = 9, rx = 6.2, h = 3.2 }: Record<string, number>) => {
  const ry = rx / 2;
  return (
    <>
      <path d={`M${cx - rx} ${cy}v${h}a${rx} ${ry} 0 0 0 ${2 * rx} 0v${-h}Z`} {...RIGHT} />
      <ellipse cx={cx} cy={cy} rx={rx} ry={ry} {...TOP} />
    </>
  );
};

/** A ball. The lune between the silhouette and an ellipse through the poles is
 *  exactly the terminator on a lit sphere. */
const Sphere = ({ cx = 12, cy = 10.6, r = 6.4 }: Record<string, number>) => (
  <>
    <circle cx={cx} cy={cy} r={r} {...TOP} />
    <path d={`M${cx} ${cy - r}a${r} ${r} 0 0 1 0 ${2 * r}a${r * 0.52} ${r} 0 0 0 0 ${-2 * r}Z`} {...RIGHT} />
  </>
);

/** A small ball — nodes in a network, dots on a face. */
const Node = ({ cx = 12, cy = 12, r = 2.4 }: Record<string, number>) => <Sphere cx={cx} cy={cy} r={r} />;

/** A flat ring lying in the ground plane — ripples, orbits, targets. */
const Ring = ({ cx = 12, cy = 14, rx = 6, w = 1.3 }: Record<string, number>) => (
  <path
    d={`M${cx - rx} ${cy}a${rx} ${rx / 2} 0 1 0 ${2 * rx} 0a${rx} ${rx / 2} 0 1 0 ${-2 * rx} 0Z`
      + `M${cx - rx + w} ${cy}a${rx - w} ${(rx - w) / 2} 0 1 0 ${2 * (rx - w)} 0a${rx - w} ${(rx - w) / 2} 0 1 0 ${-2 * (rx - w)} 0Z`}
    fillRule="evenodd" {...LEFT}
  />
);

/** A pyramid — two visible faces off one apex. */
const Cone = ({ cx = 12, apex = 3.4, hw = 5.6, base = 13 }: Record<string, number>) => {
  const hd = hw / 2;
  return (
    <>
      <path d={`M${cx} ${apex}L${cx - hw} ${base - hd}L${cx} ${base}Z`} {...LEFT} />
      <path d={`M${cx} ${apex}L${cx + hw} ${base - hd}L${cx} ${base}Z`} {...RIGHT} />
    </>
  );
};

/** Detail lying in a top face, on the projection's own slope. */
const Emboss = ({ d }: { d: string }) => <path d={d} {...CUT} />;

/** A rule embossed across a top face, from (x,y) rightward along the slope. */
const Line = ({ x, y, len, t = 1.05 }: { x: number; y: number; len: number; t?: number }) => (
  <path d={`M${x} ${y}l${len} ${len / 2}l0 ${t}l${-len} ${-len / 2}Z`} {...CUT} />
);

/* ═══════════════════════════════════════════════════════════════ identity ══ */

/** Two blocks - a company, an entity, a structure. */
export const I3Structure: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.8} />
    <Box cx={15} y0={3} hw={3.6} h={9} />
    <Box cx={8} y0={9.4} hw={3.6} h={5.4} />
  </I3>
);

/** A card with a face on it - a credential, an ID, a licence. */
export const I3Credential: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.6} />
    <Box cx={12} y0={6} hw={7} h={2.2} />
    <Node cx={8.6} cy={11.4} r={1.7} />
    <Line x={12.4} y={11.2} len={4.4} />
  </I3>
);

/** A press on a pad - an imprint, a seal, PRO work. */
export const I3Imprint: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.4} />
    <Box cx={12} y0={11.4} hw={7} h={1.8} />
    <Disc cx={12} cy={7.4} rx={3.6} h={4} />
  </I3>
);

/** A struck medal - recognition, an award, a distinction. */
export const I3Seal: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.4} cy={19.8} />
    <Disc cx={12} cy={9.4} rx={6} h={3.4} />
    <ellipse cx="12" cy="9.4" rx="2.9" ry="1.45" {...LEFT} />
  </I3>
);

/** One figure - the named person on your file. */
export const I3Person: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.2} />
    <Box cx={12} y0={12} hw={5.2} h={4.4} />
    <Sphere cx={12} cy={7.4} r={3.7} />
  </I3>
);

/** Three linked nodes - a team, a network, a market. */
export const I3Cluster: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.4} />
    <path d="M7 8.6 17 8.6 17 9.7 7 9.7Z M8.6 10.4 12.4 16.6 11.4 17.1 7.6 10.9Z M15.4 10.4 11.6 16.6 12.6 17.1 16.4 10.9Z" {...CAST} />
    <Node cx={6.8} cy={8.2} r={2.9} />
    <Node cx={17.2} cy={8.2} r={2.9} />
    <Node cx={12} cy={16.4} r={2.9} />
  </I3>
);

/** Two nodes on one bar - a partnership, an introduction. */
export const I3Join: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.6} />
    <Box cx={12} y0={10.6} hw={5.4} h={1.5} />
    <Node cx={6.2} cy={11.4} r={2.9} />
    <Node cx={17.8} cy={11.4} r={2.9} />
  </I3>
);

/* ══════════════════════════════════════════════════════════════════ trust ══ */

/** A thick plate with a mark struck in it - protection, compliance, cover. */
export const I3Aegis: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7} />
    <Box cx={12} y0={6.4} hw={6.4} h={3.4} />
    <Emboss d="M9.2 12.4 11.2 13.4 15.2 11.4 14.2 10.9 11.2 12.4 10.2 11.9Z" />
  </I3>
);

/** A coin struck with a mark - verified, filed, cleared. */
export const I3Verified: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.4} cy={19.8} />
    <Disc cx={12} cy={9.6} rx={6.2} h={3} />
    <Emboss d="M9 9.5 11.3 10.7 15.6 8.2 14.6 7.6 11.3 9.5 10 8.9Z" />
  </I3>
);

/** A ball with a mark on it - done, included, confirmed. */
export const I3Confirm: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6} cy={19.6} />
    <Sphere cx={12} cy={11} r={6.4} />
    <Emboss d="M8.6 10.8 11.2 12.2 15.6 9.6 14.6 9 11.2 10.9 9.7 10.1Z" />
  </I3>
);

/** A vault with a dial - held, sealed, private. */
export const I3Secure: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.4} />
    <Box cx={12} y0={5.6} hw={6.6} h={5.6} />
    <Disc cx={12} cy={8.9} rx={2.7} h={0.9} />
  </I3>
);

/** A cut stone - the premium tier, private clients. */
export const I3Facet: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={5.6} cy={20} />
    <path d="M12 2.8 5.8 9.2 12 12.2Z" {...TOP} />
    <path d="M12 2.8 18.2 9.2 12 12.2Z" {...LEFT} />
    <path d="M5.8 9.2 12 12.2 12 18.8Z" {...LEFT} />
    <path d="M18.2 9.2 12 12.2 12 18.8Z" {...RIGHT} />
  </I3>
);

/* ════════════════════════════════════════════════════════════ paper flow ══ */

/** A page - a document, a filing, a report. */
export const I3Doc: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.6} />
    <Box cx={12} y0={6.6} hw={6.2} h={1.6} />
    <Line x={7.4} y={10} len={4.6} />
    <Line x={9.6} y={8.7} len={4.6} />
  </I3>
);

/** A ruled book - the ledger, the books, a reconciliation. */
export const I3Ledger: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7} />
    <Box cx={12} y0={6.2} hw={6.6} h={2.6} />
    <Line x={6.4} y={9.8} len={5.4} />
    <path d="M11.6 7.2 15.4 13.1 14.4 13.6 10.6 7.7Z" {...CUT} />
  </I3>
);

/** A page of ticked rows - a checklist, an intake, a scope. */
export const I3Checklist: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.8} />
    <Box cx={12} y0={6.4} hw={6.4} h={1.7} />
    <Node cx={8.2} cy={9.9} r={1.15} />
    <Line x={10.8} y={10.5} len={3.6} />
    <Node cx={10.5} cy={8.7} r={1.15} />
    <Line x={13.1} y={9.3} len={3.6} />
  </I3>
);

/** A signed sheet - an engagement, terms agreed. */
export const I3Sign: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.8} />
    <Box cx={12} y0={6.4} hw={6.4} h={1.8} />
    <path d="M7.8 10.2c1.6-1.5 2.4.7 3.8-.2s1.4-1.9 3-1.2l.9.5c-1.5-.6-1.4.4-2.9 1.3s-2.3-1.2-3.9.3Z" {...CUT} />
  </I3>
);

/** Four blocks in quadrant - the numbers, a calculation, a quote. */
export const I3Grid: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.6} />
    <Box cx={12} y0={4.6} hw={3.2} h={1.9} />
    <Box cx={6.6} y0={7.6} hw={3.2} h={1.9} />
    <Box cx={17.4} y0={7.6} hw={3.2} h={1.9} />
    <Box cx={12} y0={10.6} hw={3.2} h={1.9} />
  </I3>
);

/** A price block - a plan, a package, a fee. */
export const I3Tag: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.8} />
    <Box cx={12} y0={7} hw={6.2} h={2.4} />
    <Node cx={9} cy={11} r={1.5} />
  </I3>
);

/** A stack of slabs - documents, filings, a paper trail. */
export const I3Docs: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7} cy={20} />
    <Box cx={12} y0={9.4} hw={6} h={1.4} />
    <Box cx={12} y0={6.3} hw={6} h={1.4} />
    <Box cx={12} y0={3.2} hw={6} h={1.4} />
  </I3>
);

/* ═══════════════════════════════════════════════════════════════════ time ══ */

/** A dial - turnaround, a window, a deadline. */
export const I3Tempo: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.4} cy={19.8} />
    <Disc cx={12} cy={9.4} rx={6} h={3.2} />
    <path d="M12 9.4 16.2 7.3 16.7 8 12.5 10.1Z" {...CUT} />
    <Node cx={12} cy={9.4} r={0.95} />
  </I3>
);

/** A dated sheet - a calendar, a filing date, a schedule. */
export const I3Calendar: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.2} />
    <Box cx={12} y0={6} hw={6.8} h={2.2} />
    <Node cx={8.8} cy={10.4} r={1.1} />
    <Node cx={12} cy={12} r={1.1} />
    <Node cx={15.2} cy={10.4} r={1.1} />
  </I3>
);

/** A ring turning - renewals, a recurring cycle. */
export const I3Cycle: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.6} cy={19.4} />
    <Ring cx={12} cy={12} rx={6.4} w={2} />
    <Node cx={18.4} cy={12} r={1.9} />
  </I3>
);

/** A ball sending ripples - a reminder, an alert, a nudge. */
export const I3Ping: Icon3D = (p) => (
  <I3 {...p}>
    <Ring cx={12} cy={16.4} rx={7.4} w={1.1} />
    <Ring cx={12} cy={16.4} rx={4.4} w={1.1} />
    <Sphere cx={12} cy={9} r={4.4} />
  </I3>
);

/* ════════════════════════════════════════════════════════════ performance ══ */

/** Rising blocks capped by a node - growth, a raise, a trajectory. */
export const I3Ascend: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.8} />
    <Box cx={6.4} y0={13.6} hw={2.6} h={3.4} />
    <Box cx={12} y0={10.4} hw={2.6} h={5} />
    <Box cx={17.6} y0={7.2} hw={2.6} h={6.6} />
    <Node cx={17.6} cy={5.4} r={1.8} />
  </I3>
);

/** Three columns - metrics, KPIs, reporting. */
export const I3Bars: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.8} />
    <Box cx={6.4} y0={12.4} hw={2.6} h={4.4} />
    <Box cx={12} y0={8.4} hw={2.6} h={6.6} />
    <Box cx={17.6} y0={10.4} hw={2.6} h={4.4} />
  </I3>
);

/** Rings on a core - a target, a score, a mandate. */
export const I3Aim: Icon3D = (p) => (
  <I3 {...p}>
    <Ring cx={12} cy={14.6} rx={7.4} w={1.2} />
    <Ring cx={12} cy={14.6} rx={4.4} w={1.2} />
    <Node cx={12} cy={12.6} r={1.9} />
  </I3>
);

/** A panel on a stem - a board, a review, a presentation. */
export const I3Board: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6} />
    <Box cx={12} y0={14.6} hw={3.4} h={1.4} />
    <Box cx={12} y0={4} hw={6.4} h={7} />
  </I3>
);

/** Sliders - tuning, an adjustment, controls. */
export const I3Tuner: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.6} />
    <Box cx={12} y0={7} hw={6.6} h={1.2} />
    <Box cx={12} y0={12.2} hw={6.6} h={1.2} />
    <Node cx={9} cy={10} r={1.9} />
    <Node cx={15} cy={15.2} r={1.9} />
  </I3>
);

/* ═══════════════════════════════════════════════════════════ capital & ops ══ */

/** An open box taking a coin - payroll, funds, something managed for you. */
export const I3Purse: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.2} />
    <ellipse cx="12" cy="3.8" rx="2.8" ry="1.4" {...TOP} />
    <path d="M9.2 3.8v1a2.8 1.4 0 0 0 5.6 0v-1Z" {...RIGHT} />
    <path
      d="M12 6.8 18.4 10 12 13.2 5.6 10Z M12 8.2 15.8 10.1 12 12 8.2 10.1Z"
      fillRule="evenodd" {...TOP}
    />
    <path d="M12 8.2 15.8 10.1 12 12 8.2 10.1Z" {...CUT} />
    <path d="M5.6 10 12 13.2v5.6l-6.4-3.2Z" {...LEFT} />
    <path d="M12 13.2 18.4 10v5.6L12 18.8Z" {...RIGHT} />
  </I3>
);

/** A beam and two pans - balance, law, a fair split. */
export const I3Balance: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.4} />
    <Box cx={12} y0={5.6} hw={6.6} h={1.2} />
    <Box cx={12} y0={9.4} hw={1.5} h={5.6} />
    <Disc cx={6} cy={13.4} rx={3} h={0.9} />
    <Disc cx={18} cy={13.4} rx={3} h={0.9} />
  </I3>
);

/** A colonnade - an institution, a bank, a regulator. */
export const I3Columns: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.8} />
    <Box cx={12} y0={14.6} hw={7} h={1.6} />
    <Box cx={7} y0={7.2} hw={1.8} h={6.4} />
    <Box cx={12} y0={9.6} hw={1.8} h={6.4} />
    <Box cx={17} y0={7.2} hw={1.8} h={6.4} />
  </I3>
);

/** A case with a handle - an engagement, a practice, a mandate. */
export const I3Case: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.4} />
    <Box cx={12} y0={4.4} hw={2} h={1.3} />
    <Box cx={12} y0={7.4} hw={6.6} h={4.6} />
  </I3>
);

/** An open crate - commerce, a basket, a transaction. */
export const I3Commerce: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.4} />
    <path
      d="M12 6.6 18.8 10 12 13.4 5.2 10Z M12 8.1 16 10.1 12 12.1 8 10.1Z"
      fillRule="evenodd" {...TOP}
    />
    <path d="M12 8.1 16 10.1 12 12.1 8 10.1Z" {...CUT} />
    <path d="M5.2 10 12 13.4v5l-6.8-3.4Z" {...LEFT} />
    <path d="M12 13.4 18.8 10v5L12 18.4Z" {...RIGHT} />
  </I3>
);

/** Two bars passing - an exchange, a transfer, a restructure. */
export const I3Exchange: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.6} />
    <Box cx={12} y0={6.6} hw={6.4} h={1.3} />
    <Box cx={12} y0={11.6} hw={6.4} h={1.3} />
    <Node cx={17.8} cy={8.2} r={1.7} />
    <Node cx={6.2} cy={15.4} r={1.7} />
  </I3>
);

/* ════════════════════════════════════════════════════════════ reach & motion ══ */

/** A mast throwing ripples - outreach, a call, first contact. */
export const I3Signal: Icon3D = (p) => (
  <I3 {...p}>
    <Ring cx={12} cy={16.6} rx={7.2} w={1.1} />
    <Box cx={12} y0={7.4} hw={1.6} h={7.4} />
    <Node cx={12} cy={5.6} r={2.2} />
  </I3>
);

/** A lit world - cross-border reach, jurisdictions. */
export const I3GlobeMesh: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.2} cy={19.6} />
    <Sphere cx={12} cy={10.6} r={6.4} />
    <path d="M5.7 11.9a6.5 2.6 0 0 0 12.6 0" fill="none" stroke="currentColor" strokeOpacity=".42" strokeWidth="1.1" />
  </I3>
);

/** Two rings interlocked - a connection, a link, an integration. */
export const I3Link: Icon3D = (p) => (
  <I3 {...p}>
    <Ring cx={8.4} cy={13} rx={5.2} w={1.7} />
    <Ring cx={15.6} cy={11} rx={5.2} w={1.7} />
  </I3>
);

/** A rising cone - a launch, a sprint, momentum. */
export const I3Launch: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={5.8} />
    <Cone cx={12} apex={3} hw={5} base={13.4} />
    <Box cx={12} y0={13.6} hw={3.2} h={1.4} />
  </I3>
);

/** A core throwing off points - new, AI, a spark of insight. */
export const I3Spark: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6} />
    <Box cx={12} y0={11.2} hw={5.8} h={1.1} />
    <Box cx={12} y0={11.2} hw={1.1} h={1.1} />
    <Sphere cx={12} cy={9.4} r={3.4} />
  </I3>
);

/** A folded ribbon - energy, speed, a surge. */
export const I3Surge: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7} />
    <path d="M5 12.2 10.4 6.4 13 7.7 7.6 13.5Z" {...TOP} />
    <path d="M11 17.6 16.4 11.8 19 13.1 13.6 18.9Z" {...LEFT} />
    <path d="M7.6 13.5 13 7.7 13 9.4 7.6 15.2Z" {...RIGHT} />
  </I3>
);

/** A needle on a dial - direction, strategy, a bearing. */
export const I3Bearing: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.4} cy={19.8} />
    <Disc cx={12} cy={9.6} rx={6} h={3} />
    <path d="M15.6 7.4 11.2 8.9 8.4 11.8 12.8 10.3Z" {...CUT} />
  </I3>
);

/* ════════════════════════════════════════════════════════════ build & craft ══ */

/** A head on a shaft - a tool, an implementation, a fix. */
export const I3Adjust: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7} />
    <path d="M8.6 12.2 11 13.4 17.6 17.6 15.2 16.4Z" {...RIGHT} />
    <path d="M11 13.4 17.6 17.6 17.6 19 11 14.8Z" {...LEFT} />
    <Box cx={8.4} y0={5.6} hw={3.6} h={3} />
  </I3>
);

/** A core with pins - a system, compute, technology. */
export const I3Core: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.6} />
    <path d="M4 9.4 7.2 11 7.2 12 4 10.4Z M16.8 11 20 9.4 20 10.4 16.8 12Z" {...CAST} />
    <Box cx={12} y0={7} hw={5.6} h={4} />
    <Disc cx={12} cy={10} rx={2.2} h={0.7} />
  </I3>
);

/** Stacked cylinders - data, a store, a source of record. */
export const I3Stack: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.4} cy={20} />
    <Disc cx={12} cy={13.4} rx={5.8} h={2.4} />
    <Disc cx={12} cy={9.6} rx={5.8} h={2.4} />
    <Disc cx={12} cy={5.8} rx={5.8} h={2.4} />
  </I3>
);

/** Slabs with air between - layers, a stack of services, a portfolio. */
export const I3Layers: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7} cy={20.2} />
    <Box cx={12} y0={11.4} hw={6} h={1.2} />
    <Box cx={12} y0={7.2} hw={6} h={1.2} />
    <Box cx={12} y0={3} hw={6} h={1.2} />
  </I3>
);

/** A sealed crate - a package, a deliverable, something shipped. */
export const I3Parcel: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.2} />
    <Box cx={12} y0={4.6} hw={6.4} h={6.2} />
    <path d="M12 6 15.8 7.9 12 9.8 8.2 7.9Z" {...LEFT} />
  </I3>
);

/** Wedges facing a core - code, software, engineering. */
export const I3Brackets: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.4} />
    <path d="M7.6 7.4 3.4 12.2 7.6 17 9.2 16.2 5.8 12.2 9.2 8.2Z" {...LEFT} />
    <path d="M16.4 7.4 20.6 12.2 16.4 17 14.8 16.2 18.2 12.2 14.8 8.2Z" {...RIGHT} />
    <Node cx={12} cy={12.2} r={1.9} />
  </I3>
);

/* ════════════════════════════════════════════════════════════ voice & craft ══ */

/** Two panels in conversation - a chat, an answer, a reply. */
export const I3Dialogue: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.4} />
    <Box cx={9.4} y0={9.6} hw={5} h={2} />
    <Box cx={14.6} y0={4.4} hw={5} h={2} />
  </I3>
);

/** A lens barrel - media, video, a portfolio of images. */
export const I3Lens: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.4} cy={19.8} />
    <Disc cx={12} cy={10} rx={6} h={3.2} />
    <Disc cx={12} cy={8.2} rx={3.2} h={1.6} />
  </I3>
);

/** Overlapping fields - design, a palette, a creative practice. */
export const I3Spectrum: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7.4} />
    <Disc cx={8.2} cy={11.4} rx={4.4} h={1.6} />
    <Disc cx={15.8} cy={11.4} rx={4.4} h={1.6} />
    <Disc cx={12} cy={8.2} rx={4.4} h={1.6} />
  </I3>
);

/** A nib on its stroke - writing, authorship, a draft. */
export const I3Stroke: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={7} />
    <path d="M5.4 18.4 14.6 8.6 16.4 9.6 7.2 19.4Z" {...LEFT} />
    <path d="M7.2 19.4 16.4 9.6 16.4 10.9 7.2 20.6Z" {...RIGHT} />
    <Node cx={17} cy={7.4} r={2.4} />
  </I3>
);

/** A peak on a base - teaching, a course, expertise shared. */
export const I3Apex: Icon3D = (p) => (
  <I3 {...p}>
    <Ground rx={6.8} />
    <Cone cx={12} apex={3.6} hw={5.6} base={13.6} />
    <Box cx={12} y0={13.8} hw={5.6} h={1.5} />
  </I3>
);

/** A core emitting - broadcast, an audience, a channel. */
export const I3Broadcast: Icon3D = (p) => (
  <I3 {...p}>
    <Ring cx={12} cy={16} rx={7.4} w={1.1} />
    <Box cx={12} y0={10.4} hw={1.4} h={4.4} />
    <Sphere cx={12} cy={7.6} r={3.6} />
  </I3>
);

/* ── Aliases ────────────────────────────────────────────────────────────────
   The flat set's names, so a page moves from flat to dimensional by changing
   one import line and nothing else. Marks the flat set has that this one folds
   into a neighbour are aliased rather than drawn twice: a second cylinder with
   a slightly different lid would read as the same object anyway. */
export {
  I3Structure as AbStructure, I3Credential as AbCredential, I3Imprint as AbImprint,
  I3Seal as AbSeal, I3Person as AbPerson, I3Cluster as AbCluster, I3Join as AbJoin,
  I3Aegis as AbAegis, I3Verified as AbVerified, I3Confirm as AbConfirm,
  I3Secure as AbSecure, I3Facet as AbFacet,
  I3Doc as AbDoc, I3Ledger as AbLedger, I3Checklist as AbChecklist, I3Sign as AbSign,
  I3Grid as AbGrid, I3Tag as AbTag,
  I3Tempo as AbTempo, I3Calendar as AbCalendar, I3Cycle as AbCycle, I3Ping as AbPing,
  I3Ascend as AbAscend, I3Bars as AbBars, I3Aim as AbAim, I3Board as AbBoard,
  I3Tuner as AbTuner,
  I3Purse as AbPurse, I3Balance as AbBalance, I3Columns as AbColumns, I3Case as AbCase,
  I3Commerce as AbCommerce, I3Exchange as AbExchange,
  I3Signal as AbSignal, I3GlobeMesh as AbGlobeMesh, I3Link as AbLink, I3Launch as AbLaunch,
  I3Spark as AbSpark, I3Surge as AbSurge, I3Bearing as AbBearing,
  I3Adjust as AbAdjust, I3Core as AbCore, I3Stack as AbStack, I3Layers as AbLayers,
  I3Parcel as AbParcel, I3Brackets as AbBrackets,
  I3Dialogue as AbDialogue, I3Lens as AbLens, I3Spectrum as AbSpectrum,
  I3Stroke as AbStroke, I3Apex as AbApex, I3Broadcast as AbBroadcast,
};

/** The prop type under the flat set's name, for files that annotate with it. */
export type AbIcon = Icon3D;
